import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import StationsOnMap from 'components/StationsOnMap/StationsOnMap'
import Title from 'components/Title/Title'
import { Point } from 'geojson'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { ILot, IMapboxElement, IRobotInfo, IStation } from 'utils/interfaces'

import AddStationModal from './AddStationModal/AddStationModal'
import AllStationsOnMap from './AllStationsOnMap/AllStationsOnMap'
import StationItem from './StationItem/StationItem'

import style from './StationListView.module.css'

const StationListView = ({
  robots,
  stations,
  lots,
  onStationsUpdated,
  onStationsChanged,
}: {
  robots: IRobotInfo[]
  stations: IStation[]
  lots: ILot[]
  onStationsUpdated: () => void
  onStationsChanged: () => void
}) => {
  const { t } = useTranslation()
  const [addingNewStation, setAddingNewStation] = useState(false)
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const [highlightedStationId, setHighlightedStationId] = useState<number | undefined>(undefined)

  const onMapInited = () => {
    const map = mapRef.current?.getMap()
    if (map) {
      setMap(map)
    }
  }

  return (
    <div className={style.container}>
      <div className={style.titleBlock}>
        <Title text={'Stations'} />{' '}
        <Button.Standart className={style.button} onClick={() => setAddingNewStation(true)}>
          Add Station
        </Button.Standart>
      </div>
      <div className={style.main}>
        <Box className={style.box}>
          <div className={style.tableWrapper}>
            <div className={style.tableHeader}>
              <div></div>
              <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Serial ID</div>
              <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Name</div>
              <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Lots</div>
              <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Assets</div>
              <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Status</div>
              <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Last action</div>
            </div>
            {stations.length ? (
              <div className={style.table}>
                {stations.map(station => (
                  <StationItem
                    key={station.id}
                    station={station}
                    stations={stations}
                    lots={lots}
                    robots={robots}
                    onStationUpdated={onStationsUpdated}
                    hightlighted={station.id === highlightedStationId}
                    onStationsChanged={onStationsChanged}
                  />
                ))}
              </div>
            ) : (
              <div className={style.emptyWrapper}>
                <div className={style.emptyTitle}>{t('robots.no_robots_title')}</div>
                <div className={style.emptyDescription}>{t('robots.no_robots_description')}</div>
              </div>
            )}
          </div>
        </Box>
        <MapboxCommon ref={mapRef} disableRuler={true} overlayItems={[]} onMapInited={onMapInited}>
          <StationsOnMap
            map={map}
            stations={stations.filter(station => station.point).map(station => station.point) as Point[]}
          />
          <AllStationsOnMap map={map} stations={stations} onStationClicked={setHighlightedStationId} />
        </MapboxCommon>
      </div>
      <AddStationModal
        isOpen={addingNewStation}
        stations={stations}
        onClose={() => setAddingNewStation(false)}
        onSave={onStationsUpdated}
        onStationsChanged={onStationsChanged}
      />
    </div>
  )
}

export default StationListView
